import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowLeft, ChevronUp, Bell, BellOff, CheckCircle2, GitMerge, History, Send, Link2 } from "lucide-react";

const STATUSES = ["Under consideration", "Planned", "In development", "In testing", "Released"];
const TERMINAL = ["Declined", "Parked"];
const TYPES = ["New feature", "Improvement", "Integration", "Performance", "Compliance"];
const PRIORITIES = ["Now", "Next", "Later"];
const EFFORTS = ["S", "M", "L", "XL"];
const VISIBILITY = ["Internal", "All users", "Customers"];
const CATEGORIES = [
  "Orders", "AI Order Intake", "ERP / Interfaces", "Deliveries & Logistics", "Article Master",
  "Conditions & Pricing", "Tax & Compliance", "Finished Goods Stock", "AI Cost Control",
  "Dashboard", "Accounts", "Opportunities", "Quotes", "Complaints", "Reports", "Admin", "Other",
];
const STATUS_COLORS = {
  "Under consideration": "bg-slate-100 text-slate-600", Planned: "bg-indigo-50 text-indigo-700",
  "In development": "bg-amber-50 text-amber-700", "In testing": "bg-blue-50 text-blue-700",
  Released: "bg-emerald-100 text-emerald-800", Declined: "bg-red-50 text-red-600", Parked: "bg-slate-200 text-slate-500",
};
const REACTIONS = ["👍", "🎉", "❤️", "🚀"];

export default function RoadmapDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isManager, canWrite, user } = useApp();
  const [item, setItem] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [history, setHistory] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [declineReason, setDeclineReason] = useState("");

  const load = useCallback(() => api.get(`/roadmap/${id}`).then((r) => { setItem(r.data); setDeclineReason(r.data.decline_reason || ""); }).catch(() => {}), [id]);
  const loadComments = useCallback(() => api.get(`/roadmap/${id}/comments`).then((r) => setComments(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); loadComments(); }, [load, loadComments]);
  useEffect(() => {
    if (isManager) {
      api.get(`/roadmap/${id}/vote-history`).then((r) => setHistory(r.data)).catch(() => {});
      api.get("/roadmap").then((r) => setAllItems(r.data)).catch(() => {});
    }
  }, [id, isManager]);

  if (!item) return <p className="text-gray-400">{t("loading")}</p>;

  const patch = async (updates) => {
    try {
      const r = await api.put(`/roadmap/${id}`, { ...item, ...updates });
      setItem(r.data);
      toast.success(t("save") + " ✓");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setStatus = (s) => {
    if (TERMINAL.includes(s) && !(declineReason || "").trim()) {
      toast.error(t("roadmap_reason_required"));
      return;
    }
    patch({ status: s, ...(TERMINAL.includes(s) ? { decline_reason: declineReason } : {}) });
  };

  const vote = async () => {
    try {
      const r = await api.post(`/roadmap/${id}/vote`);
      setItem((p) => ({ ...p, votes: r.data.votes, voted: r.data.voted, following: r.data.following }));
    } catch (e) { toast.error(errDetail(e)); }
  };
  const follow = async () => {
    try { const r = await api.post(`/roadmap/${id}/follow`); setItem((p) => ({ ...p, following: r.data.following })); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const addComment = async () => {
    if (!text.trim()) return;
    try { await api.post(`/roadmap/${id}/comments`, { text }); setText(""); loadComments(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const react = async (cid, emoji) => {
    try { await api.post(`/roadmap/comments/${cid}/react`, { emoji }); loadComments(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const delComment = async (cid) => {
    try { await api.delete(`/roadmap/comments/${cid}`); loadComments(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const doMerge = async () => {
    try { await api.post(`/roadmap/${id}/merge`, { target_id: mergeTarget }); setMergeOpen(false); toast.success(t("save") + " ✓"); navigate(`/roadmap/${mergeTarget}`); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const statusIdx = STATUSES.indexOf(item.status);
  const isTerminal = TERMINAL.includes(item.status);

  return (
    <div className="space-y-5" data-testid="roadmap-detail-page">
      <button onClick={() => navigate("/roadmap")} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1" data-testid="back-to-roadmap">
        <ArrowLeft className="h-4 w-4" />{t("roadmap")}
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <button onClick={vote} data-testid="roadmap-detail-vote"
              className={`flex flex-col items-center rounded-xl border px-4 py-2 transition-colors duration-150 ${
                item.voted ? "border-[#F5A623] bg-amber-50 text-[#1B2A4A]" : "border-gray-200 text-gray-500 hover:border-[#F5A623]"}`}>
              <ChevronUp className={`h-5 w-5 ${item.voted ? "text-[#F5A623]" : ""}`} strokeWidth={2.5} />
              <span className="text-lg font-bold font-mono" data-testid="roadmap-vote-count">{item.votes || 0}</span>
              <span className="text-[10px] uppercase tracking-wide">{t("ib_votes")}</span>
            </button>
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-mono text-sm text-gray-400">{item.item_number}</span>
                <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50">{item.type}</Badge>
                <Badge variant="outline">{item.category}</Badge>
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }} data-testid="roadmap-detail-title">{item.title}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
                <span>{t("ib_submitted_by")}: {item.submitted_by}</span>·<span>{fmtDate(item.submitted_at)}</span>
                {item.priority && <>·<Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50">{item.priority}</Badge></>}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={`${STATUS_COLORS[item.status] || ""} hover:bg-inherit text-sm`} data-testid="roadmap-status-badge">{item.status}</Badge>
            <Button size="sm" variant="outline" onClick={follow} data-testid="roadmap-follow-btn" className="rounded-full h-7 text-xs">
              {item.following ? <><BellOff className="h-3 w-3 mr-1" />{t("roadmap_unfollow")}</> : <><Bell className="h-3 w-3 mr-1" />{t("roadmap_follow")}</>}
            </Button>
          </div>
        </div>

        {/* status workflow */}
        <div className="mt-6 flex flex-wrap items-center gap-1.5" data-testid="roadmap-status-workflow">
          {STATUSES.map((s, i) => {
            const isCurrent = item.status === s;
            const done = statusIdx >= 0 && i < statusIdx;
            return (
              <button key={s} disabled={!isManager} onClick={() => isManager && setStatus(s)} data-testid={`roadmap-status-step-${i}`}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors duration-150 flex items-center gap-1 ${
                  isCurrent ? "bg-[#1B2A4A] text-white border-[#1B2A4A] font-semibold"
                    : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"} ${!isManager ? "cursor-default" : ""}`}>
                {done && <CheckCircle2 className="h-3 w-3" />}{s}
              </button>
            );
          })}
          {isManager && TERMINAL.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`roadmap-terminal-${s.toLowerCase()}`}
              className={`text-xs px-2.5 py-1.5 rounded-full border ${item.status === s ? "bg-red-600 text-white border-red-600" : "bg-white text-red-500 border-red-200 hover:bg-red-50"}`}>{s}</button>
          ))}
        </div>

        {/* decline/park reason (public) */}
        {(isTerminal || isManager) && (
          <div className="mt-4" data-testid="roadmap-reason-block">
            <Label className="text-xs text-gray-500">{t("roadmap_reason")}</Label>
            {isManager ? (
              <div className="flex gap-2 mt-1">
                <Input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} data-testid="roadmap-reason-input"
                  placeholder={t("roadmap_reason_ph")} />
                <Button size="sm" variant="outline" onClick={() => patch({ decline_reason: declineReason })}>{t("save")}</Button>
              </div>
            ) : item.decline_reason ? (
              <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2 mt-1" data-testid="roadmap-reason-text">{item.decline_reason}</p>
            ) : null}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">{t("ib_description")}</h2>
            <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="roadmap-description">{item.description}</p>
            {item.business_value && (
              <div className="mt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[#F5A623] mb-1">{t("roadmap_business_value")}</h3>
                <p className="text-sm text-gray-700">{item.business_value}</p>
              </div>
            )}
            {item.release_note && (
              <div className="mt-4 bg-emerald-50 rounded-md p-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">{t("roadmap_release_note")} {item.release_version}</h3>
                <p className="text-sm text-emerald-800">{item.release_note}</p>
              </div>
            )}
            {(item.linked_idea_ids || []).length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1"><Link2 className="h-3 w-3" />{t("roadmap_linked_ideas")}</h3>
                {item.linked_idea_ids.map((iid) => (
                  <button key={iid} onClick={() => navigate(`/ideas/${iid}`)} className="text-xs text-blue-600 hover:underline block" data-testid="roadmap-linked-idea">
                    → {iid}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Vote history (manager) */}
          {isManager && history && (
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="roadmap-vote-history">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5"><History className="h-3.5 w-3.5" />{t("roadmap_vote_history")}</h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["raw", t("roadmap_w_raw")], ["role_weighted", t("roadmap_w_role")], ["plant_weighted", t("roadmap_w_plant")]].map(([k, lbl]) => (
                  <div key={k} className="bg-gray-50 rounded-md p-3 text-center" data-testid={`roadmap-weight-${k}`}>
                    <div className="text-xl font-bold text-[#1B2A4A] font-mono">{history.weighting?.[k]}</div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">{lbl}</div>
                  </div>
                ))}
              </div>
              {history.series?.length > 0 && (
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={history.series}>
                    <XAxis dataKey="month" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip />
                    <Line type="monotone" dataKey="votes" stroke="#F5A623" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {history.voters?.map((v) => (
                  <span key={v.id} className="text-[11px] bg-gray-100 rounded-full px-2 py-0.5 text-gray-600" title={v.comment}>{v.name}</span>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="roadmap-comments">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("comments")}</h3>
            {canWrite && (
              <div className="flex gap-2 mb-4">
                <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("add_comment")} data-testid="roadmap-comment-input" className="min-h-[38px]" />
                <Button onClick={addComment} data-testid="roadmap-comment-send" className="bg-[#1B2A4A] hover:bg-[#243656]"><Send className="h-4 w-4" /></Button>
              </div>
            )}
            <div className="space-y-3">
              {comments.length === 0 && <p className="text-xs text-gray-400">–</p>}
              {comments.map((c) => (
                <div key={c.id} className="border-b border-gray-50 pb-2" data-testid="roadmap-comment">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#1B2A4A]">{c.user_name}</span>
                    <span className="text-[10px] text-gray-400">{fmtDate(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5">{c.text}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {REACTIONS.map((emo) => {
                      const count = (c.reactions?.[emo] || []).length;
                      const mine = (c.reactions?.[emo] || []).includes(user?.id);
                      return (
                        <button key={emo} onClick={() => react(c.id, emo)} data-testid={`roadmap-react-${emo}`}
                          className={`text-xs rounded-full px-1.5 py-0.5 border transition-colors ${mine ? "border-[#F5A623] bg-amber-50" : "border-transparent hover:bg-gray-100"}`}>
                          {emo}{count > 0 && <span className="ml-0.5 font-mono text-[10px]">{count}</span>}
                        </button>
                      );
                    })}
                    {isManager && <button onClick={() => delComment(c.id)} className="text-[10px] text-gray-400 hover:text-red-500 ml-2">{t("delete")}</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Manager sidebar */}
        <div className="space-y-5">
          {isManager && (
            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-testid="roadmap-manager-panel">
              <h3 className="text-sm font-semibold text-gray-700">{t("ib_details")}</h3>
              {[["category", CATEGORIES, "roadmap-field-category"], ["type", TYPES, "roadmap-field-type"],
                ["priority", PRIORITIES, "roadmap-field-priority"], ["effort", EFFORTS, "roadmap-field-effort"],
                ["visibility", VISIBILITY, "roadmap-field-visibility"]].map(([field, opts, tid]) => (
                <div key={field} className="space-y-1">
                  <Label className="text-xs text-gray-500">{t(`roadmap_${field}`)}</Label>
                  <Select value={item[field] || opts[0]} onValueChange={(v) => patch({ [field]: v })}>
                    <SelectTrigger className="h-9" data-testid={tid}><SelectValue /></SelectTrigger>
                    <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{t("roadmap_quarter")}</Label>
                <Input className="h-9" defaultValue={item.target_quarter || ""} placeholder="Q4 2026" data-testid="roadmap-field-quarter"
                  onBlur={(e) => e.target.value !== (item.target_quarter || "") && patch({ target_quarter: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{t("roadmap_release_version")}</Label>
                <Input className="h-9" defaultValue={item.release_version || ""} placeholder="v3.1" data-testid="roadmap-field-release"
                  onBlur={(e) => e.target.value !== (item.release_version || "") && patch({ release_version: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{t("roadmap_release_note")}</Label>
                <Textarea rows={2} defaultValue={item.release_note || ""} data-testid="roadmap-field-release-note"
                  onBlur={(e) => e.target.value !== (item.release_note || "") && patch({ release_note: e.target.value })} />
              </div>
              <Button variant="outline" size="sm" className="w-full" onClick={() => setMergeOpen(true)} data-testid="roadmap-merge-btn">
                <GitMerge className="h-3.5 w-3.5 mr-1" />{t("roadmap_merge")}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("roadmap_merge")}</DialogTitle>
            <DialogDescription>{t("roadmap_merge_desc")}</DialogDescription>
          </DialogHeader>
          <Select value={mergeTarget} onValueChange={setMergeTarget}>
            <SelectTrigger data-testid="roadmap-merge-target"><SelectValue placeholder={t("roadmap_merge_target")} /></SelectTrigger>
            <SelectContent>
              {allItems.filter((x) => x.id !== id).map((x) => <SelectItem key={x.id} value={x.id}>{x.item_number} · {x.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>{t("cancel")}</Button>
            <Button onClick={doMerge} disabled={!mergeTarget} data-testid="roadmap-merge-confirm" className="bg-[#1B2A4A]">{t("roadmap_merge")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
